/** PALETA MODULUI DESIGN: mobilierul, finisajele și instalația.
 *
 *  ALTĂ PALETĂ, NU ALTE GRUPE ÎN CEA VECHE. Paleta de structură are unsprezece
 *  unelte și se folosește pe tot ecranul de un metru pătrat; peste ea, un
 *  catalog de patruzeci de piese, nouă pardoseli și douăsprezece aparate ar fi
 *  însemnat o coloană de trei ecrane în care nu se mai găsește peretele drept.
 *  Sunt două meserii — cine compartimentează nu mobilează în același minut —,
 *  deci două palete, iar comutatorul dintre ele e comutatorul de mod.
 *
 *  FIECARE LUCRU SE ARATĂ CU CE E, nu cu o iconiță. O canapea e desenată cu
 *  simbolul ei de planșă, o gresie cu rostul ei la scară, o priză cu semnul din
 *  normă. E aceeași regulă ca pe pânză, și are același motiv: la a treia
 *  canapea din listă, numele nu mai deosebește nimic, forma da.
 */
import { useState } from "react";
import {
  CATEGORII_MOBILIER, numeCategorie, numePiesa, piesaSingura, TOATE_PIESELE,
} from "../../plan/arhMobilierPlan";
import { APARATE, CIRCUITE, marimeaSimbolului, numeAparat,
         numeCircuit } from "./electric";
import { t, useT } from "../../../i18n";
import {
  FinisajeDefs, PARDOSELI, PERETI, patternul, type Finisaj,
  numeFinisaj,
} from "./finisaje";
import { simbolElectric } from "./ElectricDesen";
import { Icoana } from "../icoane";

/** Un desen mic, încadrat singur. Piesele sunt desenate în metri și centrate în
 *  origine, deci `viewBox`-ul se face din măsurile lor cu o margine. */
function Miniatura({ w, h, children }: {
  w: number; h: number; children: React.ReactNode;
}) {
  const m = Math.max(w, h) * 0.62;
  return (
    <svg viewBox={`${-m} ${-m} ${2 * m} ${2 * m}`} className="ob-ed-mini"
         aria-hidden>
      {children}
    </svg>
  );
}

function Swatch({ f, activ, la }: {
  f: Finisaj; activ: boolean; la: () => void;
}) {
  return (
    <button className={`ob-ed-swatch${activ ? " on" : ""}`} onClick={la}
            title={`${numeFinisaj(f.cheie)}${f.modul
              ? ` · ${t("design.modul_finisaj", { n: (f.modul * 100).toFixed(0) })}`
              : ""}`}>
      <span className="ob-ed-swatch-fata" style={{ background: f.fond }}>
        {/* Rostul, la scara lui reală, într-un pătrat de 1,2 m — cât o dală
            mare și ceva. Așa se vede din swatch dacă o gresie e de 20 sau de
            60, ceea ce e chiar diferența dintre două finisaje care altfel au
            aceeași culoare. */}
        <svg viewBox="0 0 1.2 1.2" preserveAspectRatio="none">
          <rect x="0" y="0" width="1.2" height="1.2"
                fill={f.modul ? `url(#${patternul(f.cheie)})` : "none"} />
        </svg>
      </span>
      <span>{numeFinisaj(f.cheie)}</span>
    </button>
  );
}

export default function PaletaDesign({
  unealta, laUnealta, piesa, laPiesa, aparat, laAparat,
  finisaj, laFinisaj, laMobilier, laElectric, stransa, laStransa, doarCitire,
}: {
  unealta: string;
  laUnealta: (u: "sageata" | "mobila" | "aparat" | "pensula") => void;
  piesa: string; laPiesa: (k: string) => void;
  aparat: string; laAparat: (k: string) => void;
  finisaj: { unde: "floor" | "wall"; cheie: string };
  laFinisaj: (f: { unde: "floor" | "wall"; cheie: string }) => void;
  /** Cele două jumătăți ale baghetei, cerute separat (04.10.2026). */
  laMobilier: () => void;
  laElectric: () => void;
  stransa: boolean; laStransa: () => void;
  doarCitire: boolean;
}) {
  /** Ce secțiune e deschisă. Una singură: deschise toate, coloana are patru
   *  ecrane și niciuna nu se mai vede întreagă. */
  const t = useT();
  const [deschis, setDeschis] = useState<"mobilier" | "finisaje" | "electrice">(
    "mobilier");
  const [categorie, setCategorie] = useState(CATEGORII_MOBILIER[0]);

  const grupeAparate = [...new Set(APARATE.map((a) => a.grupa))];

  return (
    <div className="ob-ed-paleta ob-ed-paleta-design">
      {/* ROSTURILE, ȘI AICI. Pânza le emite pe ale ei, dar paleta trebuie să-și
          arate swatch-urile și când vederea e pe machetă, adică atunci când
          `<defs>`-ul pânzei nici nu e pe pagină. Id-uri duplicate sunt legale
          cât timp înseamnă ACELAȘI lucru — `url(#…)` se leagă de primul găsit —
          iar amândouă se fac din același catalog, deci nu pot diverge. */}
      <svg width="0" height="0" aria-hidden style={{ position: "absolute" }}>
        <defs><FinisajeDefs /></defs>
      </svg>
      <button className="ob-ed-strange" onClick={laStransa}
              title={t(stransa ? "paleta.desfa_titlu" : "paleta.strange_titlu")}>
        <Icoana nume={stransa ? "dreapta" : "stanga"} marime={14} />
        {!stransa && <span>{t("paleta.strange")}</span>}
      </button>

      {/* ---- BAGHETA, sus de tot, în DOUĂ -----------------------------------
          Primul lucru din paletă fiindcă e primul lucru de făcut: o cameră
          goală se mobilează o dată, automat, și abia apoi se umblă la ea.

          DOUĂ BUTOANE, NU UNUL. Mobilierul și instalația se cer separat
          (04.10.2026): cine a mutat mobila o oră vrea doar instalația peste ea,
          iar cine schimbă compartimentarea vrea doar mobilierul refăcut. Erau
          un buton și o întrebare cu «OK / Anulează», adică o alegere ascunsă
          într-un dialog — acum sunt două comenzi care se văd. Fiecare o lasă în
          pace pe cealaltă. */}
      <button className="ob-ed-bagheta" onClick={laMobilier} disabled={doarCitire}
              title={t("design.bagheta_mobilier_titlu")}>
        <span className="ob-ed-bagheta-em"><Icoana nume="bagheta" marime={20} /></span>
        <span>
          <b>{t("design.bagheta_mobilier")}</b>
          <i>{t("design.bagheta_mobilier_sub")}</i>
        </span>
      </button>
      <button className="ob-ed-bagheta" onClick={laElectric} disabled={doarCitire}
              title={t("design.bagheta_electric_titlu")}>
        <span className="ob-ed-bagheta-em"><Icoana nume="lumina" marime={20} /></span>
        <span>
          <b>{t("design.bagheta_electric")}</b>
          <i>{t("design.bagheta_electric_sub")}</i>
        </span>
      </button>

      {/* ---- săgeata, ca peste tot ---------------------------------------- */}
      <div className="ob-ed-unelte ob-ed-unelte-mod">
        {([["sageata", "design.selecteaza", "sageata"],
           ["pensula", "design.vopseste", "pensula"]] as const).map(([u, nume, semn]) => (
          <button key={u} className={`ob-ed-unealta${unealta === u ? " on" : ""}`}
                  onClick={() => laUnealta(u)} title={t(nume)}>
            <span className="ob-ed-semn"><Icoana nume={semn} marime={22} /></span>
            <span>{t(nume)}</span>
          </button>
        ))}
      </div>

      {/* ---- comutatorul de secțiune -------------------------------------- */}
      <div className="ob-seg ob-ed-seg-design">
        {([["mobilier", "design.mobilier"], ["finisaje", "design.finisaje"],
           ["electrice", "design.electrice"]] as const).map(([k, n]) => (
          <button key={k} className={deschis === k ? "on" : ""}
                  onClick={() => setDeschis(k)}>{t(n)}</button>
        ))}
      </div>

      {/* ---- MOBILIERUL --------------------------------------------------- */}
      {deschis === "mobilier" && (
        <section>
          <div className="ob-ed-file">
            {CATEGORII_MOBILIER.map((c) => (
              <button key={c} className={categorie === c ? "on" : ""}
                      onClick={() => setCategorie(c)}>{numeCategorie(c)}</button>
            ))}
          </div>
          <div className="ob-ed-galerie">
            {/* TOATE piesele: grupa «Bibliotecă» e a celor aduse din CAD,
                care n-au simbol scris de mână, ci contur — v. `Contur`. */}
            {TOATE_PIESELE.filter((p) => p.categorie === categorie).map((p) => (
              <button key={p.kind}
                      className={`ob-ed-piesa-card${
                        piesa === p.kind && unealta === "mobila" ? " on" : ""}`}
                      onClick={() => { laPiesa(p.kind); laUnealta("mobila"); }}
                      title={`${p.w.toFixed(2)} × ${p.h.toFixed(2)} m`}>
                <Miniatura w={p.w} h={p.h}>{piesaSingura(p.kind, p.w, p.h)}</Miniatura>
                <span>{numePiesa(p.kind)}</span>
                <i>{p.w.toFixed(2)} × {p.h.toFixed(2)}</i>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ---- FINISAJELE ---------------------------------------------------- */}
      {deschis === "finisaje" && (
        <section>
          <div className="ob-ed-file">
            {([["floor", "design.pardoseala"], ["wall", "design.pereti"]] as const).map(([k, n]) => (
              <button key={k} className={finisaj.unde === k ? "on" : ""}
                      onClick={() => laFinisaj({
                        unde: k,
                        cheie: (k === "floor" ? PARDOSELI : PERETI)[0].cheie,
                      })}>{t(n)}</button>
            ))}
          </div>
          <p className="ob-ed-ajutor">
            Alege un finisaj, apoi apasă pe camere cu <b>pensula</b>. Ce rămâne
            neatins păstrează implicitul felului său.
          </p>
          {[...new Set((finisaj.unde === "floor" ? PARDOSELI : PERETI)
            .map((f) => f.grupa))].map((grupa) => (
            <div key={grupa}>
              <div className="ob-ed-grup">{grupa}</div>
              <div className="ob-ed-swatches">
                {(finisaj.unde === "floor" ? PARDOSELI : PERETI)
                  .filter((f) => f.grupa === grupa)
                  .map((f) => (
                    <Swatch key={f.cheie} f={f} activ={finisaj.cheie === f.cheie}
                            la={() => {
                              laFinisaj({ unde: finisaj.unde, cheie: f.cheie });
                              laUnealta("pensula");
                            }} />
                  ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* ---- ELECTRICELE --------------------------------------------------- */}
      {deschis === "electrice" && (
        <section>
          <p className="ob-ed-ajutor">
            Aparatul pus se lipește singur de peretele cel mai apropiat. Firul
            până la tablou se desenează de la sine, pe traseul lui real.
            Caloriferele, gurile de aer și încălzirea în pardoseală le leagă
            tabul Instalații.
          </p>
          {grupeAparate.map((grupa) => (
            <div key={grupa}>
              <div className="ob-ed-grup">{grupa}</div>
              <div className="ob-ed-galerie ob-ed-galerie-el">
                {APARATE.filter((a) => a.grupa === grupa).map((a) => (
                  <button key={a.kind}
                          className={`ob-ed-piesa-card${
                            aparat === a.kind && unealta === "aparat" ? " on" : ""}`}
                          onClick={() => { laAparat(a.kind); laUnealta("aparat"); }}
                          title={`${a.h === "placa" ? t("design.la_placa")
                            : t("design.de_la_pardoseala",
                                { n: (a.h * 100).toFixed(0) })}`
                            + (a.retea ? "" : ` · ${numeCircuit(a.circuit)}`)}>
                    <Miniatura w={marimeaSimbolului(a.kind)} h={marimeaSimbolului(a.kind)}>
                      <g transform="rotate(-90)">
                        {simbolElectric(a.kind, marimeaSimbolului(a.kind))}
                      </g>
                    </Miniatura>
                    <span>{numeAparat(a.kind)}</span>
                    <i style={{ color: a.retea ? undefined : CIRCUITE[a.circuit].culoare }}>
                      {a.h === "placa" ? t("design.la_placa")
                        : `${(a.h * 100).toFixed(0)} cm`}
                    </i>
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="ob-ed-legenda-circuite">
            <div className="ob-ed-grup">{t("design.circuite")}</div>
            {Object.entries(CIRCUITE).map(([k, c]) => (
              <div key={k} className="ob-ed-circuit">
                <i style={{ background: c.culoare }} />
                <b>{numeCircuit(k)}</b>
                <span>{c.sectiune}</span>
                <em>{t(c.banda === "sus" ? "design.sub_placa"
                                         : "design.peste_pardoseala")}</em>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
